import { UploadKycDocumentApiPayload } from 'api/kyc';
import { KycDocumentsContext } from 'components/Kyc/KycDocumentsContext';
import { DocumentStatus, bvsDocumentTypes } from 'components/Kyc/constants';
import { UploadDocumentSectionProps } from 'components/Kyc/types';
import { getErrorsAsString, getMessageFromStatus } from 'components/Kyc/utils';
import { MimeType } from 'components/ui/FileUpload/constants';
import React, { useContext } from 'react';
import { UploadKycDocumentSchemaContract } from 'schemas/UploadKycDocumentSchema';
import { AppError } from 'utils/AppError';
import DocumentUpload from '../DocumentUpload';

const Fields = [
  bvsDocumentTypes.SALES_TAX,
  bvsDocumentTypes.SHOP_ESTABLISHMENT,
  bvsDocumentTypes.TRUST_SOCIETY_NGO_BUSINESS_CERTIFICATE,
  bvsDocumentTypes.BUSINESS_PAN,
  bvsDocumentTypes.GST_CERTIFICATE,
];

type FieldNames = typeof Fields[number];

export const TrustFields = ({ orgType, gstin }: UploadDocumentSectionProps) => {
  const { kycValues, updateCompanyDetailsDocument } = useContext(KycDocumentsContext);
  const data = kycValues.companyDetails;

  const onFileSelect = (
    documentType: FieldNames,
    file: File,
    upload: (payload: UploadKycDocumentApiPayload) => void,
  ) => {
    updateCompanyDetailsDocument(documentType, {
      file,
      status: DocumentStatus.UPLOADING,
      saved_filename: '',
      message: getMessageFromStatus({ status: DocumentStatus.UPLOADING }),
    });
    upload({
      documentType,
      file,
      orgType,
    });
  };

  const onSuccess = (documentType: FieldNames, response: UploadKycDocumentSchemaContract) => {
    updateCompanyDetailsDocument(documentType, {
      status: DocumentStatus.SUCCESS,
      saved_filename: response.filename || '',
      url: response.fileS3Url || '',
      message: getMessageFromStatus({ status: DocumentStatus.SUCCESS }),
    });
  };

  const onError = (documentType: FieldNames, error: AppError) => {
    updateCompanyDetailsDocument(documentType, {
      status: DocumentStatus.ERROR,
      message: getMessageFromStatus({
        status: DocumentStatus.ERROR,
        error: getErrorsAsString(error),
      }),
    });
  };

  const onFileNameClick = (url?: string) => {
    if (!url) return;
    window.open(url, '_blank');
  };

  return (
    <>
      <DocumentUpload
        wrapperlabel="Registered Trust Deed*"
        value={
          data.trust_society_ngo_business_certificate?.file?.name ||
          data.trust_society_ngo_business_certificate?.saved_filename
        }
        allowedMimeTypes={[MimeType.PDF, MimeType.JPEG, MimeType.PNG]}
        onChange={(files, upload) => {
          const file = files?.[0] || null;
          if (!file) return;
          onFileSelect('trust_society_ngo_business_certificate', file, upload);
        }}
        onSuccess={(response) => onSuccess('trust_society_ngo_business_certificate', response)}
        onError={(error) => onError('trust_society_ngo_business_certificate', error)}
        status={data.trust_society_ngo_business_certificate?.status}
        subText={data.trust_society_ngo_business_certificate?.message}
        onFilenameClick={() => onFileNameClick(data.trust_society_ngo_business_certificate?.url)}
      />
      <DocumentUpload
        wrapperlabel="Company PAN*"
        value={data.business_pan?.file?.name || data.business_pan?.saved_filename}
        allowedMimeTypes={[MimeType.PDF, MimeType.JPEG, MimeType.PNG]}
        onChange={(files, upload) => {
          const file = files?.[0] || null;
          if (!file) return;
          onFileSelect('business_pan', file, upload);
        }}
        onSuccess={(response) => onSuccess('business_pan', response)}
        onError={(error) => onError('business_pan', error)}
        status={data.business_pan?.status}
        subText={data.business_pan?.message}
        onFilenameClick={() => onFileNameClick(data.business_pan?.url)}
      />
    </>
  );
};
